import { ArrowUpRight } from 'lucide-react';
import { COMPANY } from '@/constants/testIds';
import { CEO_INTELLIGENCE, COMPANY_LOGOS } from '@/constants/content';
import { useReveal } from '@/lib/useReveal';

export default function CompanyCard({ company, span = 'default', index = 0 }) {
  const ref = useReveal();
  const spanCls =
    span === 'wide'
      ? 'md:col-span-2'
      : span === 'tall'
      ? 'md:row-span-2 md:min-h-[520px]'
      : '';
  const isCEOI = company.slug === 'ceo-intelligence';
  const logoKey = typeof company.hasLogo === 'string' ? company.hasLogo : null;
  const brandLogo = logoKey ? COMPANY_LOGOS[logoKey] : null;

  return (
    <article
      ref={ref}
      data-testid={COMPANY.card(company.slug)}
      style={{ transitionDelay: `${index * 80}ms` }}
      className={`reveal group relative bg-white rounded-2xl p-8 md:p-10 luxury-shadow border border-transparent hover:border-gold/40 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1 ${spanCls}`}
    >
      <div className="flex items-start justify-between mb-8">
        <div className="eyebrow text-taupe">{String(index + 1).padStart(2, '0')}</div>
        <ArrowUpRight
          size={22}
          strokeWidth={1.2}
          className="text-matte/40 group-hover:text-gold group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
        />
      </div>

      {isCEOI && (
        <img
          src={CEO_INTELLIGENCE.logo}
          alt="CEO Intelligence logo"
          className="h-14 w-auto object-contain mb-6"
          loading="lazy"
        />
      )}

      {brandLogo && !isCEOI && (
        <img
          src={brandLogo}
          alt={`${company.name} logo`}
          className="h-14 w-auto object-contain mb-6"
          loading="lazy"
          data-testid={`company-card-logo-${company.slug}`}
        />
      )}

      <h3 className="font-serif-display text-3xl md:text-4xl leading-[1.05] tracking-tighter text-matte mb-4">
        {company.name}
      </h3>
      <p className="font-serif-display italic text-lg md:text-xl text-taupe mb-6 leading-snug">
        {company.tagline}
      </p>
      <p className="text-sm md:text-[15px] text-matte/70 leading-relaxed mb-8 max-w-md">
        {company.description}
      </p>

      <div className="flex items-center gap-2">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald" />
        <span className="font-label text-[11px] tracking-widest uppercase text-matte/70">{company.stage}</span>
      </div>
    </article>
  );
}
